import styled from 'styled-components';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { BarChartIc } from '@dsch/dd-icons';

import { WhitePanel } from 'components/Toolkit/WhitePanel/WhitePanel';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { Price } from 'components/Toolkit/Price/Price';
import { Delivery } from 'components/Toolkit/Delivery/Delivery';
import { StripeBuyNow } from 'components/PageWrappers/AdDetails/components/AdTitleBox/components/StripeBuyNow/StripeBuyNow';
import { ManufacturerApproved } from 'components/Toolkit/Tags/ManufacturerApproved';
import { CardPaymentTag } from 'components/Toolkit/Tags/PreBuilt/CardPaymentTag';
import { DeliveryAvailableTag } from 'components/Toolkit/Tags/PreBuilt/DeliveryAvailableTag';
import { HistoryCheckTag } from 'components/Toolkit/Tags/PreBuilt/HistoryCheckTag';
import { WarrantyTag } from 'components/Toolkit/Tags/PreBuilt/WarrantyTag';
import { Tag } from 'components/Toolkit/Tags/Tag';
import { GreenLightTag } from 'components/Toolkit/Tags/PreBuilt/GreenlightTag';
import { HerdwatchTag } from 'components/Toolkit/Tags/PreBuilt/HerdwatchTag';
import { WantedTag } from 'components/Toolkit/Tags/PreBuilt/WantedTag';
import { media } from 'helpers/breakpoints';
import { ShareAd } from 'components/Toolkit/ShareAd/ShareAd';
import { PricePerMonth } from 'components/Toolkit/PricePerMonth/PricePerMonth';
import { Notification } from 'components/Toolkit/Notification/Notification';
import { handleCallStripeCheckout } from 'helpers/handleCallStripeCheckout';
import { GhostButton } from 'components/Toolkit/Button/GhostButton';

import type {
  FinanceInfo,
  FinanceLink,
} from 'components/PageWrappers/AdDetails/types';
import type { InfoTitleProps } from 'components/Toolkit/InfoTitle/InfoTitle';
import type { PriceProps } from 'components/Toolkit/Price/Price';
import { pushToDataLayer } from 'services/gtmService';
import type { IFinanceBanner } from 'types';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { useUserContext } from 'contexts/UserContext';
import { theme } from 'helpers/Theme';

import { DFPUnit } from 'components/Toolkit/DFP/DFP';
import { AD_DETAILS_DFP_CONFIG } from 'configs/dfpConfig';

import { Link } from 'components/Toolkit/Button/Link';
import NextLink from 'next/link';
import { generateFinanceLink } from 'components/PageWrappers/AdDetails/helpers';
import { fireToast } from 'helpers/Toasts';
import { accountsApi } from 'api/accountsApi';
import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
} from 'types';
import { formatAuthorizationHeader } from 'helpers/auth';
import { useAuthOptions } from 'hooks/useAuthOptions';

export interface AdTitleBoxProps extends PriceProps, InfoTitleProps {
  renderSaveAd?: ReactNode;
  isViewingOwnAd?: boolean;
  cdnUrl: string;
  handleShareAd: (t: boolean) => void;
  stripeEnabled?: boolean;
  depositEnabled?: boolean;
  adId: number;
  deliveryAvailable?: boolean;
  deliveryFee?: number;
  quantity?: number;
  buyNowSuccess?: boolean;
  renderBoostButton?: ReactNode;
  renderEditButton?: ReactNode;
  manufacturerApprovedBadge?: { manufacturer: string };
  meritsV2?: { field: string; text: string }[];
  herdwatchVerified?: boolean;
  specialOffer?: string;
  greenlightVerified?: boolean;
  cardPayments?: {
    stripeAccountId?: string;
    quantity?: string;
    shippingFee?: string;
    shippingAvailable?: boolean;
    cardPaymentType?: string;
    acceptPayment?: boolean;
  }[];
  financeInfo: FinanceInfo | null;
  financeLink?: FinanceLink;
  wanted?: boolean;
  carFinance?: {
    bannerUrl: string;
    banner?: IFinanceBanner;
  };
  buttonDisplayText?: string;
  adAnalytics?: boolean;
  priceOnRequest?: boolean;
  isSold?: boolean;
}

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const TitleAndUserActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceAndActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const PriceAndDeliveryContainer = styled.div<{
  stripeEnabled?: boolean;
  showSuccess: boolean;
}>`
  display: flex;
  order: 1;
  margin-bottom: ${({ theme, stripeEnabled, showSuccess }) =>
    stripeEnabled || showSuccess ? `${theme.spacing.M16}` : '0'};
  ${media.medium} {
    margin-bottom: 0;
  }
`;

const SStripeBuyNow = styled(StripeBuyNow)`
  flex: 1 1 100%;
  order: 3;

  ${media.medium} {
    order: 2;
    margin-left: ${({ theme }) => theme.spacing.M32};
    flex: 1 1 auto;
  }
`;

const Actions = styled.div<{
  isViewingOwnAd: boolean;
  stripeEnabled?: boolean;
  showSuccess: boolean;
}>`
  position: relative;
  min-width: ${({ isViewingOwnAd }) => (!isViewingOwnAd ? '64px' : '24px')};
  order: 2;
  margin-bottom: ${({ theme, stripeEnabled, showSuccess }) =>
    stripeEnabled || showSuccess ? `${theme.spacing.M16}` : '0'};

  ${media.medium} {
    margin-bottom: 0;
    order: 3;
  }

  @media screen and (min-width: 40em) and (min-height: 768px) {
    min-width: ${({ isViewingOwnAd }) => (!isViewingOwnAd ? '153px' : '68px')};
  }
`;

const PositionedActions = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
`;

const OwnAdButtonContainer = styled.div<{ renderBoostButton?: ReactNode }>`
  display: none;

  ${media.medium} {
    position: relative;
    top: 0;
    right: 0;
    display: grid;
    grid-template-columns: ${({ renderBoostButton }) =>
      renderBoostButton === undefined
        ? `repeat(1, 140px)`
        : `repeat(2, 140px)`};
    grid-column-gap: ${({ theme }) => theme.spacing.S8};
  }
`;

const ShareAdContainer = styled.div<{
  isViewingOwnAd: boolean;
}>`
  width: 24px;
  margin-right: ${({ isViewingOwnAd, theme }) =>
    !isViewingOwnAd ? theme.spacing.M16 : '0'};

  @media screen and (min-width: 40em) and (min-height: 768px) {
    width: 68px;
    margin-right: ${({ isViewingOwnAd, theme }) =>
      !isViewingOwnAd ? theme.spacing.M24 : '0'};
  }
`;

const Tags = styled.ul`
  margin-top: auto;
  margin-bottom: ${({ theme }) => theme.spacing.S4};
  display: flex;
  flex-wrap: wrap-reverse;
  direction: rtl;
  justify-content: flex-end;

  ${media.medium} {
    max-width: 439px;
  }
`;

const TagContainer = styled.li<{ order: number }>`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  margin-right: ${({ theme }) => theme.spacing.S8};
  order: ${({ order }) => order};
`;

const SoldTag = styled(Tag)`
  margin-top: ${({ theme }) => theme.spacing.S4};
  margin-bottom: ${({ theme }) => theme.spacing.S12};
`;

const FinanceLinkWrapper = styled.div<{ withMarginBottom?: boolean }>`
  order: 3;
  flex: 1 1 100%;
  margin-bottom: ${({ theme, withMarginBottom }) =>
    withMarginBottom ? theme.spacing.M16 : 0};
  font-family: 'Lato', sans-serif;
  display: inline-flex;
  ${media.medium} {
    order: 4;
    margin-bottom: 0;
  }
`;

export const NextToPriceContainer = styled.div`
  border-left: solid ${({ theme }) => theme.colors.GREY_LIGHT} 1px;
  padding-left: ${({ theme }) => theme.spacing.M16};
  margin-left: ${({ theme }) => theme.spacing.M16};
  width: 115px;
`;

const OuterWrapper = styled.div`
  display: flex;
  order: 3;
  flex: 1 1 100%;

  ${media.medium} {
    order: 2;
    margin-left: ${({ theme }) => theme.spacing.M32};
    flex: 1 1 auto;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  position: relative;

  ${media.medium} {
    flex-wrap: nowrap;
    padding-top: ${({ theme }) => theme.spacing.S4};
    flex-grow: 0;
  }
`;

const SuccessWrapper = styled.div`
  width: 100%;
`;

const SNotification = styled(Notification)`
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
  padding: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    padding: ${({ theme }) =>
      `${theme.spacing.S8} ${theme.spacing.M16} ${theme.spacing.S8} ${theme.spacing.S12}`};
  }
`;

const AnalyticsContainer = styled.div`
  ${({ theme }) => theme.fontSize.M14};
  margin-right: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    width: 122px;
  }
`;

const AnalyticsTextContainer = styled.p`
  display: none;

  ${media.large} {
    display: block;
    margin-left: ${({ theme }) => theme.spacing.S4};
    color: ${({ theme }) => theme.colors.BLUE};
  }
`;

function AdTitleBox({
  title,
  listItems,
  currency,
  price,
  oldPrice,
  renderSaveAd,
  className,
  isViewingOwnAd = false,
  handleShareAd,
  cdnUrl,
  deliveryAvailable = false,
  stripeEnabled,
  depositEnabled,
  adId,
  deliveryFee = 0,
  quantity = 1,
  buyNowSuccess = false,
  renderBoostButton,
  renderEditButton,
  manufacturerApprovedBadge,
  specialOffer,
  meritsV2,
  greenlightVerified,
  herdwatchVerified,
  cardPayments,
  financeInfo,
  financeLink,
  wanted,
  carFinance,
  buttonDisplayText,
  adAnalytics = true,
  priceOnRequest = false,
  isSold = false,
}: AdTitleBoxProps) {
  const [showSuccess, setShowSuccess] = useState<boolean>(buyNowSuccess);
  const { accessToken } = useAuthOptions();
  const { setIsStripeModalOpen, stripeQuantity, setStripeQuantity } =
    useAdDetailsContext();
  const {
    user,
    setIsSellerAnalyticsModalOpen,
    setSellerAnalyticsModalDisplay,
  } = useUserContext();

  async function handleCheckout() {
    if (!user?.verification.phone) {
      setIsStripeModalOpen(true);
    } else {
      handleCallStripeCheckout(adId, stripeQuantity);
    }
  }

  async function handleAnalyticsView(userId: number) {
    try {
      const { data } = await accountsApi.getSellerAnalyticsData(
        userId,
        adId,
        SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS,
        formatAuthorizationHeader(accessToken),
      );
      setIsSellerAnalyticsModalOpen(true);
      setSellerAnalyticsModalDisplay({
        title,
        selectedTab: SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        data: data.plotValues,
      });
      pushToDataLayer({
        event: 'seller_analytics',
        event_category: 'ad_management',
        event_action: 'view_ad_analytics',
        event_label: 'ad details page',
      });
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops, something went wrong! Please try again later',
      });
    }
  }

  let tags = [];
  // If it is a wanted ad we hide all other tags
  // BE still return them so that is why we need to manually hide
  if (wanted) {
    tags.push(<WantedTag />);
  } else {
    if (deliveryAvailable) {
      tags.push(<DeliveryAvailableTag />);
    }

    if (
      cardPayments &&
      cardPayments[0].acceptPayment &&
      !(depositEnabled && quantity === 0)
    ) {
      tags.push(<CardPaymentTag />);
    }

    if (meritsV2) {
      const containsHistoryCheck = meritsV2.filter(
        (merit) => merit.field === 'historyChecked',
      );
      if (containsHistoryCheck.length === 1) {
        tags.push(<HistoryCheckTag />);
      }

      const containsWarranty = meritsV2.filter(
        (merit) => merit.field === 'warranty',
      );
      if (containsWarranty.length === 1) {
        tags.push(<WarrantyTag />);
      }
    }

    if (specialOffer) {
      tags.push(<Tag text={specialOffer} textColor="GREY_DARK" />);
    }

    if (greenlightVerified) {
      tags.push(<GreenLightTag />);
    }

    if (manufacturerApprovedBadge) {
      tags.push(
        <ManufacturerApproved
          manufacturer={manufacturerApprovedBadge.manufacturer}
        />,
      );
    }

    if (herdwatchVerified) {
      tags.push(<HerdwatchTag />);
    }
  }

  const hasFinanceLink = Boolean(financeLink || financeInfo);
  const finance = {
    text: financeLink?.title || 'Get Finance Approval',
    url:
      financeLink?.url ||
      (financeInfo && generateFinanceLink(financeInfo)) ||
      '',
  };

  function handleFinanceLinkClick() {
    pushToDataLayer({
      event: 'partner_clicks',
      servicePartnerType: carFinance?.banner?.partnerType,
      servicePartnerName: carFinance?.banner?.partnerName,
    });
  }

  const financeClassImp = `${carFinance?.banner?.partnerName}-${carFinance?.banner?.partnerType}-imp`;

  return (
    <WhitePanel className={className} testId="title-box">
      <TitleAndUserActionsContainer>
        <SInfoTitle
          withH1Tag={true}
          title={title}
          listItems={listItems}
          shouldTruncate
          smallTruncationWidth={288}
          largeTruncationWidth={416}
        />
        {isViewingOwnAd && (
          <OwnAdButtonContainer renderBoostButton={renderBoostButton}>
            {renderBoostButton}
            {renderEditButton}
          </OwnAdButtonContainer>
        )}
      </TitleAndUserActionsContainer>
      {tags.length > 0 && (
        <Tags>
          {tags.map((tag, index) => (
            <TagContainer key={index} order={index + 1}>
              {tag}
            </TagContainer>
          ))}
        </Tags>
      )}
      {isSold && <SoldTag text={'Sold'} bgColor={'RED_DARK'} />}
      <PriceAndActionsContainer>
        <PriceAndDeliveryContainer
          stripeEnabled={Boolean(stripeEnabled || depositEnabled)}
          showSuccess={showSuccess}
          data-testid="stripe-delivery"
        >
          <Price
            currency={currency}
            price={price}
            oldPrice={oldPrice}
            flip={true}
            label="Price"
            priceOnRequest={priceOnRequest}
          />
          {(stripeEnabled || depositEnabled) && !financeInfo && (
            <NextToPriceContainer>
              <Delivery
                currency={currency}
                deliveryOption={!deliveryAvailable ? 'Pick-up' : deliveryFee}
              />
            </NextToPriceContainer>
          )}
          {financeInfo && (
            <NextToPriceContainer>
              <PricePerMonth financeInfo={financeInfo} />
            </NextToPriceContainer>
          )}
        </PriceAndDeliveryContainer>

        {financeInfo && financeInfo.partnerName === 'bankofirelandjournal' ? (
          <FinanceLinkWrapper
            withMarginBottom={Boolean(stripeEnabled || depositEnabled)}
            onClick={handleFinanceLinkClick}
            className={carFinance?.banner ? financeClassImp : ''}
          >
            <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_textlink} />
          </FinanceLinkWrapper>
        ) : (
          hasFinanceLink && (
            <FinanceLinkWrapper
              withMarginBottom={Boolean(stripeEnabled || depositEnabled)}
              onClick={handleFinanceLinkClick}
              className={carFinance?.banner ? financeClassImp : ''}
            >
              <Link
                ofType="SECONDARY"
                data-tracking="get-finance-approval-link"
                NextLink={NextLink}
                href={finance.url}
                target="_blank"
                rel="noopener nofollow"
              >
                {finance.text}
              </Link>
            </FinanceLinkWrapper>
          )
        )}

        {showSuccess && (
          <OuterWrapper>
            <InnerWrapper>
              <SuccessWrapper>
                <SNotification
                  type="SUCCESS"
                  title="Payment Successful"
                  text="Confirmation of your payment will be sent via email"
                  includeCloseButton={true}
                  handleCallback={() => setShowSuccess(false)}
                />
              </SuccessWrapper>
            </InnerWrapper>
          </OuterWrapper>
        )}
        {(stripeEnabled || depositEnabled) && !showSuccess && (
          <SStripeBuyNow
            cdnUrl={cdnUrl}
            quantity={quantity}
            buyNowSuccess={buyNowSuccess}
            handleCheckout={handleCheckout}
            buttonDisplayText={buttonDisplayText}
            depositEnabled={depositEnabled}
            setStripeQuantity={setStripeQuantity}
          />
        )}
        <Actions
          isViewingOwnAd={isViewingOwnAd && !adAnalytics}
          stripeEnabled={Boolean(stripeEnabled || depositEnabled)}
          showSuccess={showSuccess}
        >
          <PositionedActions>
            {isViewingOwnAd && adAnalytics ? (
              <AnalyticsContainer
                onClick={() =>
                  user?.id && title ? handleAnalyticsView(user?.id) : null
                }
              >
                <GhostButton>
                  {/* TODO: update to bar chart icon */}
                  <BarChartIc color={theme.colors.BLUE} />
                  <AnalyticsTextContainer>
                    View Analytics
                  </AnalyticsTextContainer>
                </GhostButton>
              </AnalyticsContainer>
            ) : null}
            <ShareAdContainer isViewingOwnAd={isViewingOwnAd}>
              <ShareAd isUnderlined={true} handleShareAd={handleShareAd} />
            </ShareAdContainer>
            {/*  Extra boolean test included to account for Storybook controls: */}
            {!isViewingOwnAd ? renderSaveAd : null}
          </PositionedActions>
        </Actions>
      </PriceAndActionsContainer>
    </WhitePanel>
  );
}

export { AdTitleBox };
